import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getHubData, hubSearch, askTheHub, updateHubActionStatus, createHubAction } from '../api';
import ReactMarkdown from 'react-markdown';
import ReportDetailView from './ReportDetailView';
import ComparisonView from './ComparisonView';
import {
  MagnifyingGlass, Robot, Funnel, FileText, Lightbulb, ShieldCheck,
  Vault, Factory, ArrowSquareOut, CaretDown, Star, Clock, CheckCircle,
  Warning, Info, X, SpinnerGap, ArrowRight, Plus, XCircle,
  Eye, CalendarBlank, Export, ChartLineUp
} from '@phosphor-icons/react';

const STATUS_STYLES = {
  'Approved': 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  'Pending Review': 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  'Draft': 'bg-slate-400/15 text-slate-600 border-slate-400/30',
  'Evidence Gap': 'bg-red-500/15 text-red-700 border-red-500/30',
};

const FW_BADGE = {
  'IFRS S2': 'bg-blue-500/15 text-blue-700',
  'GRI': 'bg-emerald-500/15 text-emerald-700',
  'SASB': 'bg-violet-500/15 text-violet-700',
  'TNFD': 'bg-teal-500/15 text-teal-700',
  'CDP': 'bg-amber-500/15 text-amber-700',
  'CBAM': 'bg-orange-500/15 text-orange-700',
};

const PERIOD_GROUPS = ['2026', '2025', '2024'];

const SEVERITY_STYLES = {
  'High': { bg: 'bg-red-500/10', text: 'text-red-700', border: 'border-red-500/25', icon: Warning },
  'Medium': { bg: 'bg-amber-500/10', text: 'text-amber-700', border: 'border-amber-500/25', icon: Info },
  'Critical': { bg: 'bg-red-600/15', text: 'text-red-800', border: 'border-red-600/30', icon: XCircle },
  'Informational': { bg: 'bg-blue-500/10', text: 'text-blue-700', border: 'border-blue-500/25', icon: Info },
};

const PRIORITY_STYLES = {
  'High': 'text-red-700 bg-red-500/10',
  'Medium': 'text-amber-700 bg-amber-500/10',
  'Low': 'text-slate-600 bg-slate-400/10',
};

const ACTION_STATUS_STYLES = {
  'Open': 'bg-blue-500/15 text-blue-700',
  'In Progress': 'bg-amber-500/15 text-amber-700',
  'Closed': 'bg-emerald-500/15 text-emerald-700',
  'Cancelled': 'bg-slate-400/15 text-slate-500',
};

const FILTER_OPTIONS = {
  group: ['APAC Manufacturing Group'],
  plant: ['All', 'Plant A', 'Plant B', 'Plant C'],
  product: ['All', 'Industrial Coatings', 'Protective Resin', 'Adhesive Film', 'Specialty Compounds'],
  supplier: ['All', 'ChemCo Asia', 'Pacific Materials', 'Resin Dynamics', 'GreenMat Solutions'],
  month: ['March 2026', 'February 2026', 'January 2026'],
  framework: ['All', 'IFRS S2', 'GRI', 'SASB', 'TNFD', 'CDP', 'CBAM'],
};

const SAMPLE_PROMPTS = [
  "Why did Plant B emissions rise?",
  "Show KPIs missing evidence",
  "Which product has highest carbon cost risk?",
  "Summarize March EESG performance",
];

export default function ReportsHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ group: 'APAC Manufacturing Group', plant: 'All', product: 'All', supplier: 'All', month: 'March 2026', framework: 'All' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [askQuery, setAskQuery] = useState('');
  const [askResult, setAskResult] = useState(null);
  const [askLoading, setAskLoading] = useState(false);
  const [showAskPanel, setShowAskPanel] = useState(false);
  const [expandedFramework, setExpandedFramework] = useState(null);
  const [showCreateAction, setShowCreateAction] = useState(false);
  const [newAction, setNewAction] = useState({ type: 'Assign CAPA', title: '', owner: '', due_date: '', priority: 'Medium' });
  const [activeReportId, setActiveReportId] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [reportFwFilter, setReportFwFilter] = useState('All');
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: hubData } = await getHubData();
      setData(hubData);
    } catch (err) {
      console.error('Hub data fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.trim().length < 2) { setSearchResults([]); setShowSearch(false); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data: res } = await hubSearch(q);
        setSearchResults(res.suggestions || []);
        setShowSearch(true);
      } catch { setSearchResults([]); }
    }, 300);
  };

  const handleAsk = async (question) => {
    const q = question || askQuery;
    if (!q.trim()) return;
    setAskQuery(q);
    setAskLoading(true);
    setShowAskPanel(true);
    try {
      const { data: res } = await askTheHub(q, filters);
      setAskResult(res);
    } catch (err) {
      setAskResult({ answer: 'Failed to get response. Please try again.', question: q });
    } finally {
      setAskLoading(false);
    }
  };

  const handleUpdateAction = async (actionId, status) => {
    try {
      await updateHubActionStatus(actionId, status);
      fetchData();
    } catch (err) { console.error('Action update failed:', err); }
  };

  const handleCreateAction = async () => {
    if (!newAction.title.trim() || !newAction.owner.trim()) return;
    try {
      await createHubAction(newAction);
      setShowCreateAction(false);
      setNewAction({ type: 'Assign CAPA', title: '', owner: '', due_date: '', priority: 'Medium' });
      fetchData();
    } catch (err) { console.error('Action creation failed:', err); }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-hub-loading">
        <SpinnerGap size={32} className="text-primary animate-spin" />
      </div>
    );
  }

  // Show report detail view when a report is selected
  if (activeReportId) {
    return <ReportDetailView reportId={activeReportId} onBack={() => setActiveReportId(null)} />;
  }

  // Show comparison view
  if (showComparison) {
    return <ComparisonView onBack={() => setShowComparison(false)} />;
  }

  const { page_meta, reports, insights, frameworks, evidence_summary, evidence_objects, actions } = data;

  // Filter reports by framework
  const filteredReports = reportFwFilter === 'All' ? reports : reports.filter(r => r.framework === reportFwFilter);

  // Group reports by year
  const groupedReports = {};
  PERIOD_GROUPS.forEach(year => {
    const matching = filteredReports.filter(r => r.period?.includes(year) || r.period_start?.startsWith(year));
    if (matching.length > 0) groupedReports[year] = matching;
  });

  return (
    <div className="space-y-5" data-testid="reports-hub">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight" data-testid="hub-title">
            {page_meta.title}
          </h1>
          <p className="font-body text-sm text-text-secondary mt-1">{page_meta.subtitle}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/10 text-emerald-700 rounded-full text-[11px] font-body font-medium">
            <CheckCircle size={12} weight="fill" /> {page_meta.pipeline_status}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-background border border-border rounded-full text-[11px] font-body text-text-secondary">
            <Clock size={12} /> Refreshed: {new Date(page_meta.last_refresh).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-background border border-border rounded-full text-[11px] font-body text-text-secondary">
            <CalendarBlank size={12} /> {page_meta.current_period}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-[11px] font-body font-medium">
            <Factory size={12} weight="fill" /> {page_meta.scope}
          </span>
          <button
            data-testid="compare-periods-btn"
            onClick={() => setShowComparison(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-[11px] font-heading font-semibold hover:bg-[#2B3523] transition-all"
          >
            <ChartLineUp size={14} weight="duotone" />
            Compare 2024→2026
          </button>
        </div>
      </div>

      {/* ── Search + Ask the Hub ── */}
      <div className="bg-surface border border-border rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative" ref={searchRef}>
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary" />
              <input
                data-testid="hub-search-input"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowSearch(true)}
                placeholder="Search reports, metrics, plants, suppliers, evidence..."
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg font-body text-sm text-text-primary placeholder:text-tertiary focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            {showSearch && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-lg z-40 max-h-64 overflow-y-auto">
                {searchResults.map((r, i) => (
                  <button key={i} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-background transition-colors text-left" onClick={() => { setShowSearch(false); setSearchQuery(''); }}>
                    <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-body font-semibold uppercase">{r.type}</span>
                    <span className="font-body text-sm text-text-primary truncate">{r.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            data-testid="ask-hub-toggle"
            onClick={() => setShowAskPanel(!showAskPanel)}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg font-heading text-sm font-semibold hover:bg-[#2B3523] transition-all whitespace-nowrap"
          >
            <Robot size={18} weight="duotone" />
            Ask the Hub
          </button>
        </div>

        {/* Ask the Hub Panel */}
        {showAskPanel && (
          <div className="mt-4 border-t border-border pt-4" data-testid="ask-hub-panel">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                {SAMPLE_PROMPTS.map((p, i) => (
                  <button key={i} onClick={() => handleAsk(p)} className="px-3 py-1.5 bg-background border border-border rounded-full text-xs font-body text-text-secondary hover:border-primary hover:text-primary transition-all">
                    {p}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="ask-hub-input"
                  value={askQuery}
                  onChange={(e) => setAskQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                  placeholder="Ask about performance, evidence gaps, or what to prioritize..."
                  className="flex-1 px-4 py-2.5 bg-background border border-border rounded-lg font-body text-sm text-text-primary placeholder:text-tertiary focus:outline-none focus:border-primary"
                />
                <button
                  data-testid="ask-hub-submit"
                  onClick={() => handleAsk()}
                  disabled={askLoading || !askQuery.trim()}
                  className="px-4 py-2.5 bg-primary text-white rounded-lg font-heading text-sm font-semibold hover:bg-[#2B3523] transition-all disabled:opacity-50"
                >
                  {askLoading ? <SpinnerGap size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />}
                </button>
              </div>
              {askLoading && (
                <div className="flex items-center gap-2 p-4 bg-background rounded-lg">
                  <SpinnerGap size={18} className="text-primary animate-spin" />
                  <span className="font-body text-sm text-text-secondary">Analyzing with AI...</span>
                </div>
              )}
              {askResult && !askLoading && (
                <div className="bg-background border border-border rounded-lg p-4 max-h-96 overflow-y-auto" data-testid="ask-hub-result">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Robot size={16} className="text-primary" />
                      <span className="text-xs font-body font-semibold text-primary uppercase tracking-wider">AI Response</span>
                    </div>
                    <button onClick={() => { setAskResult(null); setShowAskPanel(false); }} className="text-tertiary hover:text-text-primary"><X size={14} /></button>
                  </div>
                  <div className="prose prose-sm max-w-none font-body text-text-primary text-sm leading-relaxed">
                    <ReactMarkdown>{askResult.answer}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Filter Bar ── */}
      <div className="bg-surface border border-border rounded-xl p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 mr-1">
            <Funnel size={14} className="text-tertiary" />
            <span className="text-[10px] font-body font-semibold uppercase tracking-[0.15em] text-tertiary">Filters</span>
          </div>
          {Object.entries(FILTER_OPTIONS).map(([key, options]) => (
            <div key={key} className="relative">
              <select
                data-testid={`filter-${key}`}
                value={filters[key]}
                onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                className="appearance-none pl-3 pr-7 py-1.5 bg-background border border-border rounded-lg text-xs font-body text-text-primary focus:outline-none focus:border-primary cursor-pointer"
              >
                {options.map(o => <option key={o} value={o}>{key === 'group' ? o : o}</option>)}
              </select>
              <CaretDown size={10} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-tertiary pointer-events-none" />
            </div>
          ))}
          <button
            onClick={() => setFilters({ group: 'APAC Manufacturing Group', plant: 'All', product: 'All', supplier: 'All', month: 'March 2026', framework: 'All' })}
            className="ml-auto text-[10px] font-body font-medium text-tertiary hover:text-primary transition-colors uppercase tracking-wider"
          >
            Reset
          </button>
        </div>
      </div>

      {/* ── Content Grid: Row 1 — My Reports (full width) ── */}
      <div className="bg-surface border border-border rounded-xl p-5" data-testid="my-reports-panel">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <FileText size={18} weight="duotone" className="text-primary" />
            <h2 className="font-heading text-base font-semibold text-text-primary">My Reports</h2>
            <span className="text-[10px] font-body text-tertiary">({filteredReports.length} reports)</span>
          </div>
          <div className="flex gap-1 flex-wrap">
            {['All', 'IFRS S2', 'GRI', 'SASB', 'TNFD', 'CDP', 'CBAM'].map(fw => (
              <button key={fw} onClick={() => setReportFwFilter(fw)} data-testid={`report-fw-filter-${fw.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-body font-medium transition-all ${reportFwFilter === fw ? 'bg-primary text-white' : 'bg-background border border-border text-text-secondary hover:border-primary hover:text-primary'}`}>
                {fw}
              </button>
            ))}
          </div>
        </div>

        {Object.keys(groupedReports).length === 0 && (
          <p className="text-sm font-body text-text-secondary text-center py-6">No reports match the selected framework.</p>
        )}

        {Object.entries(groupedReports).map(([year, yearReports]) => (
          <div key={year} className="mb-4 last:mb-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-px flex-1 bg-border/50" />
              <span className="text-[10px] font-heading font-bold text-tertiary uppercase tracking-widest">{year}</span>
              <div className="h-px flex-1 bg-border/50" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {yearReports.map((r) => (
                <button
                  key={r.report_id}
                  data-testid={`report-card-${r.report_id}`}
                  onClick={() => setActiveReportId(r.report_id)}
                  className="flex flex-col p-3.5 bg-background rounded-lg border border-border/50 hover:border-primary/40 hover:shadow-sm transition-all text-left group"
                >
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-body font-semibold ${FW_BADGE[r.framework] || 'bg-slate-400/15 text-slate-600'}`}>{r.framework}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-body font-medium border ${STATUS_STYLES[r.status] || 'bg-slate-400/15 text-slate-600'}`}>{r.status}</span>
                    {r.favorite && <Star size={11} weight="fill" className="text-amber-500" />}
                  </div>
                  <p className="font-body text-sm font-medium text-text-primary group-hover:text-primary transition-colors line-clamp-2">{r.title}</p>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
                    <span className="text-[10px] font-body text-text-secondary">{r.owner}</span>
                    <span className="text-[10px] font-body text-tertiary">{r.period}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Content Grid: Row 2 — Smart Insights ── */}
      <div className="bg-surface border border-border rounded-xl p-5" data-testid="smart-insights-panel">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb size={18} weight="duotone" className="text-amber-600" />
          <h2 className="font-heading text-base font-semibold text-text-primary">Smart Insights</h2>
        </div>
          <div className="space-y-3">
            {insights.map((ins) => {
              const sev = SEVERITY_STYLES[ins.severity] || SEVERITY_STYLES['Medium'];
              const SevIcon = sev.icon;
              return (
                <div key={ins.insight_id} className={`p-4 rounded-lg border ${sev.border} ${sev.bg} cursor-pointer hover:shadow-sm transition-all`} data-testid={`insight-card-${ins.insight_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <SevIcon size={16} weight="fill" className={`${sev.text} flex-shrink-0 mt-0.5`} />
                      <div className="min-w-0">
                        <p className={`font-body text-sm font-semibold ${sev.text}`}>{ins.title}</p>
                        <p className="font-body text-xs text-text-secondary mt-1 line-clamp-2">{ins.summary}</p>
                        {ins.drivers && ins.drivers.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {ins.drivers.slice(0, 3).map((d, i) => (
                              <span key={i} className="px-2 py-0.5 bg-white/60 border border-border/50 rounded text-[10px] font-body text-text-secondary">{d}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${sev.text} ${sev.bg}`}>{ins.severity}</span>
                      <span className="text-[10px] font-body text-tertiary">Conf: {Math.round(ins.confidence_score * 100)}%</span>
                      {ins.estimated_cost_impact_usd > 0 && (
                        <span className="text-[10px] font-body font-medium text-red-600">${(ins.estimated_cost_impact_usd / 1000).toFixed(0)}K impact</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
      </div>

      {/* ── Content Grid: Row 3 — Framework Navigator | Evidence Vault ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Framework Navigator */}
        <div className="bg-surface border border-border rounded-xl p-5" data-testid="framework-navigator">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={18} weight="duotone" className="text-primary" />
            <h2 className="font-heading text-base font-semibold text-text-primary">Framework Navigator</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {frameworks.map((fw) => {
              const isExpanded = expandedFramework === fw.framework;
              const pct = fw.completion_pct;
              const barColor = pct >= 85 ? '#10b981' : pct >= 70 ? '#f59e0b' : '#ef4444';
              const txtColor = pct >= 85 ? 'text-emerald-600' : pct >= 70 ? 'text-amber-600' : 'text-red-600';
              return (
                <button
                  key={fw.framework}
                  data-testid={`framework-pill-${fw.framework.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setExpandedFramework(isExpanded ? null : fw.framework)}
                  className={`relative p-3 rounded-lg border text-left transition-all ${isExpanded ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-background hover:border-primary/40'}`}
                >
                  <p className="font-heading text-sm font-bold text-text-primary">{fw.framework}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="flex-1 h-1.5 bg-border/60 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                    </div>
                    <span className={`text-[10px] font-body font-bold ${txtColor}`}>{pct}%</span>
                  </div>
                  {isExpanded && (
                    <div className="mt-2.5 pt-2.5 border-t border-border/50 space-y-1">
                      <p className="text-[10px] font-body text-text-secondary">Metrics: <span className="font-semibold text-text-primary">{fw.mapped_metrics}</span></p>
                      <p className="text-[10px] font-body text-text-secondary">Evidence: <span className="font-semibold text-text-primary">{fw.evidence_sufficiency_pct}%</span></p>
                      <p className="text-[10px] font-body text-text-secondary">Missing: <span className="font-semibold text-red-600">{fw.missing_items}</span></p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Evidence Vault */}
        <div className="bg-surface border border-border rounded-xl p-5" data-testid="evidence-vault">
          <div className="flex items-center gap-2 mb-4">
            <Vault size={18} weight="duotone" className="text-primary" />
            <h2 className="font-heading text-base font-semibold text-text-primary">Evidence Vault</h2>
          </div>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'KPIs Reported', value: evidence_summary.total_kpis_reported, color: 'text-text-primary' },
              { label: 'Fully Evidenced', value: evidence_summary.kpis_with_full_evidence, color: 'text-emerald-600' },
              { label: 'Missing Evidence', value: evidence_summary.kpis_missing_evidence, color: 'text-red-600' },
              { label: 'Pending Approvals', value: evidence_summary.pending_approvals, color: 'text-amber-600' },
            ].map((stat, i) => (
              <div key={i} className="p-2.5 bg-background rounded-lg border border-border/50 text-center">
                <p className={`font-heading text-xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
          {/* Factor Library & Audit */}
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex items-center justify-between p-2.5 bg-background rounded-lg border border-border/50">
              <span className="text-[11px] font-body text-text-secondary">Factor Library</span>
              <span className="text-[11px] font-body font-semibold text-primary">{evidence_summary.latest_factor_library_version}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-background rounded-lg border border-border/50">
              <span className="text-[11px] font-body text-text-secondary">Latest Audit Event</span>
              <span className="text-[11px] font-body font-medium text-text-primary truncate ml-2">{evidence_summary.latest_audit_event}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-background rounded-lg border border-border/50">
              <span className="text-[11px] font-body text-text-secondary">Linked Files</span>
              <span className="text-[11px] font-body font-semibold text-text-primary">{evidence_summary.linked_files_count}</span>
            </div>
          </div>
          {/* Evidence Objects Table */}
          <div className="mt-3 max-h-48 overflow-y-auto">
            <table className="w-full text-[11px] font-body">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-1.5 text-tertiary font-semibold uppercase tracking-wider">Metric</th>
                  <th className="text-left py-1.5 text-tertiary font-semibold uppercase tracking-wider">Owner</th>
                  <th className="text-left py-1.5 text-tertiary font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {evidence_objects.map((ev) => (
                  <tr key={ev.evidence_id} className="border-b border-border/30 hover:bg-background/50">
                    <td className="py-1.5 text-text-primary font-medium">{ev.metric_name}</td>
                    <td className="py-1.5 text-text-secondary">{ev.owner}</td>
                    <td className="py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${ev.approval_status === 'Approved' ? 'bg-emerald-500/15 text-emerald-700' : ev.approval_status === 'Pending' ? 'bg-amber-500/15 text-amber-700' : 'bg-red-500/15 text-red-700'}`}>
                        {ev.approval_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Content Grid: Row 3 — Report Factory | Action Loop ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Report Factory */}
        <div className="bg-surface border border-border rounded-xl p-5" data-testid="report-factory">
          <div className="flex items-center gap-2 mb-4">
            <Factory size={18} weight="duotone" className="text-primary" />
            <h2 className="font-heading text-base font-semibold text-text-primary">Report Factory</h2>
          </div>
          <p className="text-xs font-body text-text-secondary mb-4">Generate, preview, and schedule trusted reports.</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Monthly EESG Pack', desc: 'Full EESG performance summary' },
              { label: 'Plant Performance Pack', desc: 'Site-level deep dive' },
              { label: 'Board Sustainability Pack', desc: 'Executive summary for board' },
              { label: 'Supplier Compliance Pack', desc: 'Supplier audit & data status' },
              { label: 'Customer Carbon Disclosure', desc: 'Product-level carbon data' },
              { label: 'Assurance Evidence Pack', desc: 'Audit-ready evidence bundle' },
            ].map((tpl, i) => (
              <div key={i} className="p-3 bg-background rounded-lg border border-border/50 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer group" data-testid={`report-template-${i}`}>
                <p className="font-body text-sm font-medium text-text-primary group-hover:text-primary transition-colors">{tpl.label}</p>
                <p className="text-[10px] font-body text-text-secondary mt-0.5">{tpl.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2.5 mt-4 pt-3 border-t border-border/50">
            <button className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white rounded-lg text-xs font-heading font-semibold hover:bg-[#2B3523] transition-all" data-testid="report-create-btn">
              <Plus size={14} /> Create
            </button>
            <button className="flex items-center gap-1.5 px-3 py-2 bg-background border border-border text-text-primary rounded-lg text-xs font-heading font-semibold hover:border-primary transition-all" data-testid="report-preview-btn">
              <Eye size={14} /> Preview
            </button>
            <button className="flex items-center gap-1.5 px-3 py-2 bg-background border border-border text-text-primary rounded-lg text-xs font-heading font-semibold hover:border-primary transition-all" data-testid="report-export-btn">
              <Export size={14} /> Export
            </button>
            <button className="flex items-center gap-1.5 px-3 py-2 bg-background border border-border text-text-primary rounded-lg text-xs font-heading font-semibold hover:border-primary transition-all" data-testid="report-schedule-btn">
              <CalendarBlank size={14} /> Schedule
            </button>
          </div>
        </div>

        {/* Action Loop */}
        <div className="bg-surface border border-border rounded-xl p-5" data-testid="action-loop">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ArrowSquareOut size={18} weight="duotone" className="text-primary" />
              <h2 className="font-heading text-base font-semibold text-text-primary">Action Loop</h2>
            </div>
            <button
              data-testid="create-action-btn"
              onClick={() => setShowCreateAction(!showCreateAction)}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-body font-medium hover:bg-primary/20 transition-all"
            >
              <Plus size={12} /> New Action
            </button>
          </div>

          {/* Create Action Form */}
          {showCreateAction && (
            <div className="mb-4 p-3 bg-background rounded-lg border border-primary/30 space-y-2.5" data-testid="create-action-form">
              <div className="grid grid-cols-2 gap-2">
                <select value={newAction.type} onChange={(e) => setNewAction({ ...newAction, type: e.target.value })} className="px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs font-body text-text-primary focus:outline-none focus:border-primary">
                  <option>Assign CAPA</option>
                  <option>Request Supplier Data</option>
                  <option>Raise Work Order</option>
                  <option>Update Narrative</option>
                </select>
                <select value={newAction.priority} onChange={(e) => setNewAction({ ...newAction, priority: e.target.value })} className="px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs font-body text-text-primary focus:outline-none focus:border-primary">
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>
              <input value={newAction.title} onChange={(e) => setNewAction({ ...newAction, title: e.target.value })} placeholder="Action title..." className="w-full px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs font-body text-text-primary placeholder:text-tertiary focus:outline-none focus:border-primary" />
              <div className="grid grid-cols-2 gap-2">
                <input value={newAction.owner} onChange={(e) => setNewAction({ ...newAction, owner: e.target.value })} placeholder="Owner..." className="px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs font-body text-text-primary placeholder:text-tertiary focus:outline-none focus:border-primary" />
                <input type="date" value={newAction.due_date} onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })} className="px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs font-body text-text-primary focus:outline-none focus:border-primary" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleCreateAction} className="px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-heading font-semibold hover:bg-[#2B3523] transition-all">Create</button>
                <button onClick={() => setShowCreateAction(false)} className="px-3 py-1.5 bg-background border border-border text-text-secondary rounded-lg text-xs font-body hover:text-text-primary transition-colors">Cancel</button>
              </div>
            </div>
          )}

          {/* Actions List */}
          <div className="space-y-2.5">
            {actions.map((act) => (
              <div key={act.action_id} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border/50 hover:border-primary/30 transition-all" data-testid={`action-card-${act.action_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-body font-semibold">{act.type}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-body font-semibold ${PRIORITY_STYLES[act.priority] || ''}`}>{act.priority}</span>
                  </div>
                  <p className="font-body text-sm font-medium text-text-primary truncate">{act.title}</p>
                  <p className="font-body text-[11px] text-text-secondary mt-0.5">{act.owner} &middot; Due: {act.due_date}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${ACTION_STATUS_STYLES[act.status] || ''}`}>{act.status}</span>
                  {act.status !== 'Closed' && act.status !== 'Cancelled' && (
                    <select
                      value={act.status}
                      onChange={(e) => handleUpdateAction(act.action_id, e.target.value)}
                      className="appearance-none px-2 py-1 bg-surface border border-border rounded text-[10px] font-body text-text-primary cursor-pointer focus:outline-none"
                    >
                      <option>Open</option>
                      <option>In Progress</option>
                      <option>Closed</option>
                      <option>Cancelled</option>
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
